import { workspaceApiFetch } from '@affine/core/modules/cloud';

/** One page of the workspace and where it stands in the search index. */
export interface IndexedDocState {
  id: string;
  title: string;
  indexed: boolean;
  excluded: boolean;
}

async function request(
  workspaceId: string,
  query: string,
  variables: Record<string, unknown>
) {
  const response = await workspaceApiFetch(
    '/graphql',
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    },
    {
      workspaceId,
      category: 'metadata',
      key: `embedding:${query.slice(0, 48)}`,
      mode: 'background',
      priority: 'background',
    }
  );
  const body = await response.json();
  if (body.errors?.length) throw new Error(body.errors[0].message);
  return body.data;
}

/** All pages of the workspace, those still waiting for the index first. */
export async function fetchIndexedDocStates(
  workspaceId: string
): Promise<IndexedDocState[]> {
  const data = await request(
    workspaceId,
    `query EmbeddingDocDetails($id: String!, $pagination: PaginationInput!) {
      workspace(id: $id) {
        indexedDocIds
        docs(pagination: $pagination) {
          edges { node { id title trashed } }
        }
        embedding { allIgnoredDocs { docId } }
      }
    }`,
    { id: workspaceId, pagination: { first: 500, offset: 0 } }
  );
  const workspace = data?.workspace;
  if (!workspace) return [];

  const indexed = new Set<string>(workspace.indexedDocIds ?? []);
  const excluded = new Set<string>(
    (workspace.embedding?.allIgnoredDocs ?? []).map(
      (doc: { docId: string }) => doc.docId
    )
  );
  const docs: { id: string; title: string; trashed: boolean }[] = (
    workspace.docs?.edges ?? []
  ).map((edge: { node: unknown }) => edge.node);

  return docs
    .filter(doc => doc.id !== workspaceId && !doc.trashed)
    .map(doc => ({
      id: doc.id,
      title: doc.title ?? '',
      indexed: indexed.has(doc.id),
      excluded: excluded.has(doc.id),
    }))
    .sort(
      (a, b) =>
        Number(a.excluded) - Number(b.excluded) ||
        Number(a.indexed) - Number(b.indexed) ||
        a.title.localeCompare(b.title)
    );
}
